import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDays, fmtH } from "@/lib/people";
import { toast } from "sonner";

// mode "entitlement": set the yearly total; mode "adjust": add or remove hours with a reason.
export function BalanceEditDialog({ orgId, mode, person, type, year, yearLabel, current, onClose, onSaved }) {
  const day = person.day_hours;
  const [unit, setUnit] = useState(day ? "days" : "hours");
  const [value, setValue] = useState(mode === "entitlement" && current.has_entitlement ? String(day ? +(current.entitlement / day).toFixed(2) : current.entitlement) : "");
  const [direction, setDirection] = useState("add");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const hours = Number(value || 0) * (unit === "days" ? day : 1);

  const save = async () => {
    if (!value || Number.isNaN(hours)) return toast.error("Enter an amount");
    setSaving(true);
    try {
      const base = { user_id: person.user_id, leave_type_id: type.id, year };
      if (mode === "entitlement") await api.post(`/workspace/leave/entitlements?org_id=${orgId}`, { ...base, hours, note: note || null });
      else await api.post(`/workspace/leave/adjustments?org_id=${orgId}`, { ...base, hours: direction === "add" ? hours : -hours, note });
      toast.success(mode === "entitlement" ? "Entitlement saved" : "Balance adjusted");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="balance-edit-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "entitlement" ? "Set entitlement" : "Adjust balance"} · {person.name}</DialogTitle>
          <DialogDescription>{type.name}, {yearLabel}. {day ? `One day = ${day}h for ${person.name.split(" ")[0]}.` : "No work pattern — enter hours."} Every change is recorded in the balance history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          {mode === "adjust" && (
            <div className="flex gap-1.5 rounded-lg bg-[#F4F6F9] p-1">
              {[["add", "Add"], ["remove", "Remove"]].map(([k, l]) => (
                <button key={k} onClick={() => setDirection(k)} data-testid={`adjust-${k}`}
                  className={`flex-1 rounded-md py-1.5 text-sm font-medium ${direction === k ? "bg-white text-[#0B1729] shadow-sm" : "text-[#1E2D44]/55"}`}>{l}</button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label>{mode === "entitlement" ? "Yearly entitlement" : "Amount"}</Label>
              <Input type="number" min="0" step="0.25" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" data-testid="balance-amount" />
            </div>
            {day > 0 && (
              <div className="flex gap-1 rounded-lg bg-[#F4F6F9] p-1">
                {["days", "hours"].map((u) => (
                  <button key={u} onClick={() => setUnit(u)} data-testid={`balance-unit-${u}`}
                    className={`rounded-md px-3 py-1.5 text-sm ${unit === u ? "bg-white font-medium text-[#0B1729] shadow-sm" : "text-[#1E2D44]/55"}`}>{u}</button>
                ))}
              </div>
            )}
          </div>
          {value && <p className="text-xs text-[#1E2D44]/55" data-testid="balance-equivalent">= {fmtH(hours)}{day ? ` (${fmtDays(hours, day)})` : ""}</p>}
          <div><Label>{mode === "adjust" ? "Reason (required)" : "Note (optional)"}</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder={mode === "adjust" ? "e.g. Carried over from last year, as agreed" : ""} data-testid="balance-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || (mode === "adjust" && !note.trim())} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="balance-save-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
